<template>
  <f7-page name="microsoft365" class="m365-page" @page:beforein="entrar" @page:afterout="salir">
    <f7-navbar title="Microsoft 365" :back-link="$t('atras')"></f7-navbar>

    <div class="m365p">
      <div class="hero">
        <h1>Microsoft 365 · Empleados existentes</h1>
        <div class="sub">
          Incorpora a las personas que ya tienen cuenta con Business Premium: se vinculan sin recrear la cuenta ni cambiar
          su licencia o contraseña, y se añaden al grupo de su perfil y a SIMECAL EQUIPO. Primero se prepara un
          diagnóstico, que solo lee; después un responsable elige a quién incorporar y lo ejecuta.
        </div>
      </div>

      <div class="fallo" v-if="errorEstado">
        <div class="t">⚠️ {{ errorEstado }}</div>
      </div>

      <template v-if="estado">
        <!-- Qué está preparado y qué no, antes de pulsar nada: así un destino sin configurar no se
             descubre a mitad de la ejecución. -->
        <div class="card">
          <h3>Estado de la integración <span class="m365-modo" :class="'modo-' + estado.modo">{{ rotuloModo }}</span></h3>
          <p class="aviso" v-if="estado.motivoModo">{{ estado.motivoModo }}
            <span v-if="estado.modo === 'DESACTIVADO'">La activa el administrador del sistema en la configuración del servidor.</span>
          </p>
          <p class="nota" v-if="estado.modo === 'SIMULADO'">
            Simulación: trabaja contra un tenant ficticio y no toca Microsoft. Solo se diagnostican las fichas del ERP cuyo
            correo empieza por «ficticio.», para que aquí no aparezca ningún empleado real. Cuentas ficticias disponibles:
            {{ estado.cuentasFicticias.join(', ') }}.
          </p>
          <div class="tblwrap">
            <table class="tbl">
              <thead><tr><th>Perfil</th><th>Grupo de colaboración / correo</th><th>Grupo de seguridad (Intune)</th></tr></thead>
              <tbody>
                <tr v-for="d in estado.destinos" :key="d.perfil">
                  <td class="b">{{ d.nombre }}</td>
                  <td>{{ d.colaboracionCorreo }} · <span :class="d.colaboracionConfigurada ? 'ok' : 'pend'">{{ d.colaboracionConfigurada ? 'configurado' : 'sin configurar' }}</span></td>
                  <td>{{ d.seguridadNombre }} · <span :class="d.seguridadConfigurada ? 'ok' : 'pend'">{{ d.seguridadConfigurada ? 'configurado' : 'sin configurar' }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="nota">
            {{ estado.equipo.nombre }}: <span :class="estado.equipo.configurada ? 'ok' : 'pend'">{{ estado.equipo.configurada ? 'configurado' : 'sin configurar' }}</span>
            · {{ estado.licencia.nombre }}: <span :class="estado.licencia.configurada ? 'ok' : 'pend'">{{ estado.licencia.configurada ? 'configurado' : 'sin configurar' }}</span>
            <template v-if="estado.archivo">
              · {{ estado.archivo.nombre }}: <span :class="estado.archivo.configurada ? 'ok' : 'pend'">{{ estado.archivo.configurada ? 'configurado' : 'sin configurar' }}</span>
            </template>
            · Los grupos de correo no aplican directivas ni aplicaciones al PC: eso es Intune, y aquí no se toca.
          </div>
        </div>

        <div class="barra">
          <div class="accion">
            <button class="m365btn" :disabled="!puedePreparar || ocupado" @click="preparar">Preparar diagnóstico</button>
            <span class="motivo" v-if="motivoPreparar">{{ motivoPreparar }}</span>
          </div>
          <span class="nota" v-if="lote">
            Diagnóstico pedido por {{ lote.creadoPor }} el {{ fechaHora(lote.creadoEn) }}
            <template v-if="lote.diagnosticadoEn"> · preparado {{ fechaHora(lote.diagnosticadoEn) }}</template>
            <template v-if="lote.vigenteHasta && lote.estado === 'DIAGNOSTICADO'"> · vale para ejecutar hasta {{ fechaHora(lote.vigenteHasta) }}</template>
          </span>
        </div>

        <div class="estado" v-if="lote && lote.estado === 'DIAGNOSTICANDO'">
          <f7-preloader></f7-preloader><span>Preparando el diagnóstico: se consulta Microsoft persona a persona, sin cambiar nada…</span>
        </div>
        <!-- La ejecución guarda en la API a quién se eligió y por dónde va: si el servidor se reinicia a
             medias, la cola la retoma desde la persona siguiente y esta pantalla sigue sondeando. -->
        <div class="estado" v-if="lote && lote.estado === 'EJECUTANDO'">
          <f7-preloader></f7-preloader><span>{{ lote.ejecutar && lote.ejecutar.motivo }}</span>
        </div>
        <div class="fallo" v-if="lote && lote.estado === 'ERROR'">
          <div class="t">⚠️ No se ha podido preparar el diagnóstico</div>
          <div class="m">{{ lote.error }}</div>
        </div>

        <template v-if="lote && lote.filas && lote.filas.length">
          <div class="kpis">
            <div class="kpi"><div class="lab">Personas</div><div class="val">{{ lote.totales.personas }}</div></div>
            <div class="kpi"><div class="lab">Con cambios pendientes</div><div class="val">{{ lote.totales.conCambios }}</div></div>
            <div class="kpi"><div class="lab">Ya configuradas</div><div class="val">{{ lote.totales.yaConfigurados }}</div></div>
            <div class="kpi"><div class="lab">Para revisión</div><div class="val">{{ lote.totales.paraRevision }}</div></div>
          </div>

          <!-- Resultado de la ejecución, por persona y total. Se actualiza solo mientras queda algo en curso. -->
          <div class="card" v-if="lote.resultados && lote.resultados.length">
            <h3>Resultado de la ejecución</h3>
            <div class="cs">Ejecutado por {{ lote.ejecutadoPor }} el {{ fechaHora(lote.ejecutadoEn) }}.
              <span v-for="(v, k) in resumenResultados" :key="k"> {{ k }}: <b>{{ v }}</b>.</span>
            </div>
            <div class="tblwrap">
              <table class="tbl">
                <thead><tr><th>Persona</th><th>Estado</th><th>Detalle</th></tr></thead>
                <tbody>
                  <tr v-for="r in lote.resultados" :key="'r-' + r.empleadoId">
                    <td><a v-if="puedeAbrirFicha" @click="abrirFicha(r.empleadoId)">{{ r.nombre || r.username }}</a><span v-else>{{ r.nombre || r.username }}</span></td>
                    <td><span class="chip" :class="'e-' + r.estado">{{ rotuloSolicitud(r.estado) }}</span></td>
                    <td>{{ r.resumen }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <ul class="avisos" v-if="noEncoladas.length">
              <li v-for="e in noEncoladas" :key="'ne-' + e.empleadoId">{{ nombreDe(e.empleadoId) }}: {{ e.motivo }}</li>
            </ul>
          </div>

          <div class="card">
            <h3>Diagnóstico</h3>
            <!-- La lista es larga: se busca escribiendo, por nombre, usuario o correo (regla §9). -->
            <div class="filtros">
              <input type="search" v-model.trim="busqueda" placeholder="Buscar por nombre, usuario o correo" />
              <select v-model="filtro">
                <option value="todas">Todas</option>
                <option value="cambios">Con cambios pendientes</option>
                <option value="ya">Ya configuradas</option>
                <option value="revision">Para revisión</option>
              </select>
              <label class="todas" v-if="lote.estado === 'DIAGNOSTICADO'">
                <input type="checkbox" :checked="todasMarcadas" @change="marcarTodas($event.target.checked)" />
                Marcar todas las que tienen cambios
              </label>
            </div>
            <div class="tblwrap">
              <table class="tbl">
                <thead><tr>
                  <th></th><th>Persona</th><th>Rol → perfil</th><th>Cuenta en Microsoft</th><th>Business Premium</th>
                  <th>Grupos</th><th>Qué se haría</th>
                </tr></thead>
                <tbody>
                  <tr v-for="f in filasVisibles" :key="f.empleadoId" :class="{ revision: !f.elegible }">
                    <td>
                      <input type="checkbox" v-if="f.conCambios && lote.estado === 'DIAGNOSTICADO'" v-model="seleccion" :value="f.empleadoId" />
                    </td>
                    <td>
                      <div class="b"><a v-if="puedeAbrirFicha" @click="abrirFicha(f.empleadoId)">{{ f.nombre || f.username }}</a><span v-else>{{ f.nombre || f.username }}</span></div>
                      <div class="alias">{{ f.username }} · {{ f.correo || 'sin correo' }}</div>
                    </td>
                    <td>{{ f.rol || 'sin rol' }} → {{ f.perfil || 'sin perfil' }}</td>
                    <td>
                      <div>{{ rotuloCuenta(f.cuenta) }}</div>
                      <div class="alias" v-if="f.cuentaUpn">{{ f.cuentaNombre }} · {{ f.cuentaUpn }}</div>
                      <div class="alias" v-if="f.vinculo">Ya vinculada: {{ f.vinculo }}</div>
                    </td>
                    <td>{{ rotuloLicencia(f.licencia) }}</td>
                    <td>
                      <div v-for="(g, i) in f.pertenencias" :key="f.empleadoId + '-g' + i">
                        <span class="chip" :class="'e-' + g.estado">{{ rotuloPaso(g.estado) }}</span> {{ g.correo || g.nombre }}
                        <div class="nota-g" v-if="g.estado === 'REQUIERE_INTERVENCION'">{{ g.mensaje }}</div>
                      </div>
                    </td>
                    <td>
                      <template v-if="f.elegible">
                        <div v-for="(p, i) in f.pendientes" :key="f.empleadoId + '-p' + i">{{ p }}</div>
                        <div v-if="!f.pendientes.length" class="ok">Ya configurado: no hay nada que hacer.</div>
                      </template>
                      <ul class="motivos" v-else>
                        <li v-for="(m, i) in f.motivos" :key="f.empleadoId + '-m' + i">{{ m }}</li>
                      </ul>
                    </td>
                  </tr>
                  <tr v-if="!filasVisibles.length"><td colspan="7" class="vacio">Nadie coincide con la búsqueda.</td></tr>
                </tbody>
              </table>
            </div>
            <div class="nota">
              Para revisión: se corrige la ficha del empleado (o su tarjeta Microsoft 365) y se prepara otro diagnóstico.
              Nadie para revisión recibe accesos por esta pantalla.
            </div>
            <div class="barra">
              <div class="accion">
                <button class="m365btn" :disabled="!puedeEjecutar || ocupado" @click="ejecutar">
                  Ejecutar sincronización ({{ seleccion.length }} {{ seleccion.length === 1 ? 'persona' : 'personas' }})
                </button>
                <span class="motivo" v-if="motivoEjecutar">{{ motivoEjecutar }}</span>
              </div>
            </div>
          </div>
        </template>
        <div class="estado" v-else-if="lote && lote.estado === 'DIAGNOSTICADO'">
          No hay ninguna ficha que diagnosticar.
        </div>
        <div class="fallo" v-if="errorAccion"><div class="m">{{ errorAccion }}</div></div>
      </template>
    </div>
  </f7-page>
</template>

<script>
import Microsoft365, { SIN_RESPUESTA } from '../../js/db/Microsoft365.js';
import PermisosEmpleados from '../empleados/permisos.js';
import cerrojoDeLaSesion from '../../js/utils/cerrojoDeLaSesion.js';

const RECARGA_MS = 4000;
const ROTULO_PASO = {
  PENDIENTE: 'Falta', EN_CURSO: 'En curso', COMPLETADO: 'Completado', YA_CONFIGURADO: 'Ya configurado',
  REQUIERE_INTERVENCION: 'Requiere intervención', ERROR: 'Error', NO_APLICA: 'No aplica',
  SIN_VERIFICAR: 'Declarado a mano, sin verificar'
};
const ROTULO_SOLICITUD = {
  PENDIENTE: 'Pendiente', EN_CURSO: 'En curso', REQUIERE_INTERVENCION: 'Requiere intervención', ERROR: 'Error',
  SIN_VERIFICAR: 'Sin verificar', COMPLETADA: 'Completada', SUSTITUIDA: 'Sustituida por otra solicitud'
};
const ROTULO_RESULTADO = {
  resultado_pendiente: 'Pendientes', resultado_en_curso: 'En curso', resultado_completada: 'Completadas',
  resultado_requiere_intervencion: 'Requieren intervención', resultado_error: 'Con error',
  resultado_sin_verificar: 'Sin verificar', resultado_sustituida: 'Sustituidas'
};
const ROTULO_CUENTA = { ENCONTRADA: 'Encontrada', NO_ENCONTRADA: 'No tiene', AMBIGUA: 'Varias cuentas (ambigua)', NO_COMPROBADA: 'No comprobada' };
const ROTULO_LICENCIA = {
  ACTIVA_DIRECTA: 'Activa (directa)', ACTIVA_GRUPO: 'Activa (por grupo)', ACTIVA_DIRECTA_Y_GRUPO: 'Activa (directa y por grupo)',
  ERROR: 'En error', PENDIENTE: 'Aplicándose', SIN_LICENCIA: 'No tiene', NO_COMPROBADA: 'No comprobada'
};

/**
 * #1044 · Diagnóstico y sincronización de empleados existentes con Microsoft 365.
 *
 * La pantalla no decide nada: quién es elegible, qué se haría y si se puede ejecutar lo contesta la
 * API, que es la misma regla que aplica luego al ejecutar. Aquí solo se elige a quién incorporar de
 * entre los que el diagnóstico deja elegir.
 */
export default {
  data: function() {
    return {
      estado: null,
      lote: null,
      seleccion: [],
      busqueda: '',
      filtro: 'todas',
      ocupado: false,
      errorEstado: null,
      errorAccion: null,
      temporizador: null,
      turno: 0,
      // Si se ha querido leer el lote con una acción en vuelo. Ver `leer`.
      lecturaPendiente: false,
      // Si la pantalla está a la vista. Al salir, las respuestas que estén en camino ya no pintan
      // ni programan otra recarga: si no, seguirían pidiendo el lote cada pocos segundos fuera de la
      // pantalla, y al volver a entrar se sumaría un sondeo más.
      activa: false
    };
  },
  computed: {
    rotuloModo: function() {
      return { SIMULADO: 'Simulación', REAL: 'Conectado', DESACTIVADO: 'Desactivado' }[this.estado.modo] || this.estado.modo;
    },
    puedePreparar: function() {
      return !this.motivoPreparar;
    },
    motivoPreparar: function() {
      if (!this.estado) return null;
      if (this.estado.modo === 'DESACTIVADO') return 'La integración está desactivada en este servidor.';
      if (!this.estado.puedeGestionar) {
        return 'Solo lo pueden hacer los responsables autorizados de Microsoft 365 (' + this.estado.rolesGestion.join(', ')
          + '). Lo concede un administrador en la pantalla de Usuarios.';
      }
      if (this.lote && this.lote.estado === 'DIAGNOSTICANDO') return 'Ya se está preparando uno.';
      return null;
    },
    puedeEjecutar: function() {
      return !this.motivoEjecutar;
    },
    motivoEjecutar: function() {
      if (!this.lote || !this.lote.ejecutar) return null;
      if (!this.lote.ejecutar.disponible) return this.lote.ejecutar.motivo;
      if (!this.seleccion.length) return 'Marca a las personas que quieres incorporar.';
      return null;
    },
    filasVisibles: function() {
      const q = this.busqueda.toLowerCase();
      return (this.lote ? this.lote.filas : []).filter(f => {
        if (this.filtro === 'cambios' && !f.conCambios) return false;
        if (this.filtro === 'ya' && !(f.elegible && !f.conCambios)) return false;
        if (this.filtro === 'revision' && f.elegible) return false;
        if (!q) return true;
        return [f.nombre, f.username, f.correo].some(v => v && v.toLowerCase().includes(q));
      });
    },
    todasMarcadas: function() {
      const conCambios = this.lote ? this.lote.filas.filter(f => f.conCambios) : [];
      return conCambios.length > 0 && conCambios.every(f => this.seleccion.includes(f.empleadoId));
    },
    noEncoladas: function() {
      return this.lote ? (this.lote.ejecuciones || []).filter(e => !e.encolada) : [];
    },
    resumenResultados: function() {
      const r = {};
      const t = this.lote ? this.lote.totales : {};
      Object.keys(t).forEach(k => {
        if (ROTULO_RESULTADO[k]) r[ROTULO_RESULTADO[k]] = t[k];
      });
      return r;
    },
    puedeAbrirFicha: function() {
      return PermisosEmpleados.abrirFicha();
    }
  },
  methods: {
    entrar: function() {
      this.activa = true;
      this.errorEstado = null;
      const turno = ++this.turno;
      Microsoft365.estado().then(r => {
        if (!this.vigente(turno)) return;
        this.estado = r;
        if (r.puedeGestionar && r.modo !== 'DESACTIVADO') {
          this.cargarUltimo();
        }
      }).catch(e => {
        if (this.vigente(turno)) this.errorEstado = (e && e.message) || 'No se ha podido leer el estado de la integración.';
      });
    },
    salir: function() {
      this.activa = false;
      // Cualquier respuesta pedida antes de salir deja de ser «la última»: no pinta ni reprograma.
      this.turno++;
      this.pararRecarga();
    },
    /** Si una respuesta pedida en ese turno todavía vale: la pantalla sigue a la vista y no se ha pedido otra después. */
    vigente: function(turno) {
      return this.activa && turno === this.turno;
    },
    pararRecarga: function() {
      if (this.temporizador) {
        clearTimeout(this.temporizador);
        this.temporizador = null;
      }
    },
    /**
     * La única lectura del lote, por la que pasan la carga al entrar y el sondeo. Con «Preparar» o
     * «Ejecutar» en vuelo no sale: volvería con el lote de antes de la acción y, por ser posterior,
     * dejaría sin pintar la respuesta de la acción. Pasa al salir y volver a entrar mientras tanto.
     * Se apunta y, cuando la acción termina, se lee el último lote.
     */
    leer: function(peticion, alFallar) {
      if (this.ocupado) {
        this.lecturaPendiente = true;
        return;
      }
      const turno = ++this.turno;
      peticion().then(l => {
        if (this.vigente(turno)) this.pintar(l);
      }).catch(e => {
        if (this.vigente(turno)) alFallar(e);
      });
    },
    terminarAccion: function() {
      this.ocupado = false;
      if (this.lecturaPendiente) {
        this.lecturaPendiente = false;
        if (this.activa) this.cargarUltimo();
      }
    },
    cargarUltimo: function() {
      this.leer(() => Microsoft365.ultimaSincronizacion(), e => {
        this.errorAccion = (e && e.message) || 'No se ha podido leer la última sincronización.';
      });
    },
    recargar: function() {
      if (!this.lote || !this.activa) return;
      const id = this.lote.id;
      // Una vuelta de recarga fallida no borra lo que se ve: la siguiente lo vuelve a intentar.
      this.leer(() => Microsoft365.sincronizacion(id), () => this.programar());
    },
    pintar: function(l) {
      const otro = !this.lote || !l || this.lote.id !== l.id;
      this.lote = l || null;
      if (otro) this.seleccion = [];
      // Solo se puede marcar a quien el diagnóstico deja: lo que ya no lo está se desmarca.
      const validos = this.lote ? this.lote.filas.filter(f => f.conCambios).map(f => f.empleadoId) : [];
      this.seleccion = this.seleccion.filter(id => validos.includes(id));
      this.programar();
    },
    programar: function() {
      this.pararRecarga();
      if (!this.activa) return;
      const enCurso = this.lote && (this.lote.estado === 'DIAGNOSTICANDO' || this.lote.estado === 'EJECUTANDO'
        || (this.lote.resultados || []).some(r => r.estado === 'PENDIENTE' || r.estado === 'EN_CURSO'));
      if (enCurso) {
        this.temporizador = setTimeout(() => this.recargar(), RECARGA_MS);
      }
    },
    preparar: function() {
      cerrojoDeLaSesion(this, 'ocupado');
      this.errorAccion = null;
      // El sondeo del lote anterior se para antes de pedir: si saltara mientras tanto, su recarga
      // pasaría a ser la petición vigente y la respuesta de esta se descartaría.
      this.pararRecarga();
      const turno = ++this.turno;
      Microsoft365.prepararDiagnostico().then(l => {
        if (this.vigente(turno)) this.pintar(l);
      }).catch(e => {
        this.fallarAccion(e, turno, 'No se ha podido pedir el diagnóstico.');
      }).then(() => this.terminarAccion());
    },
    /**
     * Si la acción no ha salido, lo que se ve sigue siendo el lote anterior y el sondeo lo mantiene al
     * día. Si no ha contestado a tiempo, no se sabe: puede que el servidor la recibiera, así que se
     * dice y, al soltar la acción, se lee el último lote, que es donde se ve si se aplicó.
     */
    fallarAccion: function(e, turno, porDefecto) {
      if (e && e.sinRespuesta) {
        this.errorAccion = SIN_RESPUESTA + ' Puede que se haya aplicado: abajo está el estado que se ha vuelto a leer;'
          + ' compruébalo antes de repetirlo.';
        this.lecturaPendiente = true;
        return;
      }
      this.errorAccion = (e && e.message) || porDefecto;
      if (this.vigente(turno)) this.programar();
    },
    marcarTodas: function(marcar) {
      this.seleccion = marcar ? this.lote.filas.filter(f => f.conCambios).map(f => f.empleadoId) : [];
    },
    ejecutar: function() {
      const n = this.seleccion.length;
      const simulado = this.estado.modo === 'SIMULADO' ? ' en el tenant SIMULADO' : ' en Microsoft 365';
      this.$f7.dialog.confirm('Se van a incorporar ' + n + (n === 1 ? ' persona' : ' personas') + simulado
        + ': vincular su cuenta y añadir las pertenencias que les faltan. No se crea ninguna cuenta ni se asigna ninguna'
        + ' licencia.', 'Ejecutar sincronización', () => {
        cerrojoDeLaSesion(this, 'ocupado');
        this.errorAccion = null;
        // Lo mismo que al preparar: una recarga pendiente no puede pisar la respuesta de la ejecución.
        this.pararRecarga();
        const turno = ++this.turno;
        Microsoft365.ejecutar(this.lote.id, this.seleccion.slice()).then(l => {
          if (this.vigente(turno)) this.pintar(l);
        }).catch(e => {
          this.fallarAccion(e, turno, 'No se ha podido ejecutar la sincronización.');
        }).then(() => this.terminarAccion());
      });
    },
    abrirFicha: function(id) {
      this.$f7router.navigate('/empleados/view/' + encodeURIComponent(id) + '/');
    },
    nombreDe: function(id) {
      const f = this.lote && this.lote.filas.find(x => x.empleadoId === id);
      return f ? (f.nombre || f.username) : id;
    },
    fechaHora: function(v) {
      return v ? new Date(v).toLocaleString('es-ES', { timeZone: 'Europe/Madrid', dateStyle: 'short', timeStyle: 'short' }) : '';
    },
    rotuloPaso: function(e) { return ROTULO_PASO[e] || e; },
    rotuloSolicitud: function(e) { return ROTULO_SOLICITUD[e] || e; },
    rotuloCuenta: function(c) { return ROTULO_CUENTA[c] || c; },
    rotuloLicencia: function(l) { return ROTULO_LICENCIA[l] || l; }
  },
  beforeDestroy: function() {
    this.salir();
  }
};
</script>

<style scoped>
.m365p { max-width: 1300px; margin: 0 auto; padding: 16px; font-size: 13px; }
.hero h1 { margin: 0 0 4px; font-size: 22px; color: #23355b; }
.hero .sub { color: #555; font-size: 13px; }
.card { border: 1px solid #e3e6ee; border-radius: 6px; padding: 12px 16px; background: #fff; margin: 12px 0; }
.card h3 { margin: 0 0 6px; font-size: 15px; color: #23355b; }
.card .cs { font-size: 12px; color: #777; margin-bottom: 8px; }
.m365-modo { padding: 2px 10px; border-radius: 12px; font-size: 12px; font-weight: bold; color: #fff; background: #5f6368; margin-left: 6px; }
.modo-SIMULADO { background: #a34800; }
.modo-REAL { background: #22355b; }
.nota { color: #666; font-size: 12px; }
.aviso { color: #b45309; font-weight: bold; }
.ok { color: #2e7d32; }
.pend { color: #b45309; }
.barra { display: flex; flex-wrap: wrap; align-items: flex-start; gap: 12px; margin: 12px 0; }
.accion { display: flex; flex-direction: column; align-items: flex-start; gap: 2px; max-width: 520px; }
.motivo { color: #b45309; font-size: 12px; }
.m365btn { border: 1px solid #23355b; background: #23355b; color: #fff; border-radius: 4px; padding: 4px 12px; cursor: pointer; }
.m365btn:disabled { opacity: .45; cursor: not-allowed; }
.estado { display: flex; align-items: center; gap: 8px; padding: 12px 0; color: #555; }
.fallo { border: 1px solid #f3c2c2; background: #fff5f5; border-radius: 6px; padding: 12px 16px; margin: 12px 0; }
.fallo .t { font-weight: 600; color: #a61b1b; margin-bottom: 4px; }
.kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; margin: 12px 0; }
.kpi { border: 1px solid #e3e6ee; border-radius: 6px; padding: 10px 14px; background: #fff; }
.kpi .lab { font-size: 12px; color: #666; text-transform: uppercase; letter-spacing: .03em; }
.kpi .val { font-size: 24px; font-weight: 700; color: #23355b; }
.filtros { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-bottom: 8px; }
.filtros input[type=search] { min-width: 260px; border: 1px solid #ccd; border-radius: 4px; padding: 4px 6px; }
.filtros select { border: 1px solid #ccd; border-radius: 4px; padding: 4px 6px; background: #fff; }
.filtros .todas { display: flex; align-items: center; gap: 4px; color: #555; }
.tblwrap { overflow-x: auto; }
.tbl { width: 100%; border-collapse: collapse; font-size: 12px; }
.tbl th { text-align: left; font-weight: 600; color: #555; border-bottom: 1px solid #e3e6ee; padding: 6px 8px; }
.tbl td { border-bottom: 1px solid #f0f1f5; padding: 6px 8px; vertical-align: top; }
.tbl tr.revision td { background: #fffdf5; }
.tbl .b { font-weight: 600; }
.tbl .alias { color: #888; font-size: 11px; }
.tbl .vacio { text-align: center; color: #777; padding: 16px; }
.tbl a { cursor: pointer; }
.nota-g { color: #b45309; font-size: 11px; }
.motivos { margin: 0; padding-left: 16px; color: #6b4e00; }
.avisos { margin: 8px 0 0; padding: 8px 16px 8px 28px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; color: #6b4e00; }
.chip { display: inline-block; padding: 1px 8px; border-radius: 10px; font-size: 11px; font-weight: bold; color: #fff; background: #5f6368; white-space: nowrap; }
/* Colores con contraste suficiente para texto blanco (4,5:1 o más). */
.e-EN_CURSO { background: #0b5cad; }
.e-COMPLETADO, .e-COMPLETADA, .e-YA_CONFIGURADO { background: #1e7b34; }
.e-REQUIERE_INTERVENCION, .e-SIN_VERIFICAR { background: #a34800; }
.e-ERROR { background: #b3261e; }
.e-PENDIENTE { background: #5f6368; }
</style>
